import type {
  AgendaOrden,
  CorregirMovimiento,
  Empacar,
  EstadoInventario,
  RegistrarMovimiento,
  Enviar,
  Recibir,
  Producir,
  AlistamientoOrden,
  DespacharOrden,
  OrdenPorAlistar,
  AvisoResumen,
  CandidataCau,
  CierreDelMes,
  CrearMotivoDevolucion,
  DevolucionesDeOrden,
  MotivoDevolucion,
  RegistrarDevolucion,
  DetalleDelIndicador,
  ResultadoImportacionOtif,
  CerrarRestriccion,
  ConfiguracionCamion,
  CrearRestriccion,
  GuardarVehiculo,
  MarcarDiaSinCamion,
  CambiarEstadoUsuario,
  ConfirmarPago,
  CrearProrroga,
  FormatoCau,
  MotivoCau,
  ProrrogaResumen,
  RegistrarRespuestaCau,
  CrearTipoDeduccion,
  Conciliacion,
  ConfiguracionFacturacion,
  OrdenesFacturables,
  EstadoSiigo,
  FacturaDetalle,
  FacturaResumen,
  RecaudoResumen,
  RegistrarDeduccion,
  RegistrarFactura,
  RegistrarRecaudo,
  ReciboEnviado,
  ResultadoImportacionAvisos,
  RegistrarFacturaGasto,
  ConversacionCompleta,
  ConversacionResumen,
  PreguntaPatroclo,
  DocumentoCompleto,
  DocumentoPatroclo,
  ResultadoSubidaDocumento,
  VisibilidadDocumento,
  RespuestaPatroclo,
  FiltroTrazas,
  PaginaTrazas,
  RangoTrazas,
  ResumenTrazas,
  TrazaCompleta,
  ResumenDeGastos,
  ResultadoImportacionSiigo,
  TipoDeduccion,
  Credenciales,
  CrearUsuario,
  OrdenDetalle,
  OrdenResumen,
  RegistrarRecepcion,
  ReiniciarClave,
  ResultadoBusqueda,
  ResultadoImportacion,
  UsuarioResumen,
  UsuarioSesion,
} from '@manejo-alcanza/contracts';

/**
 * Cliente de la API.
 *
 * El token CSRF se guarda en memoria, no en `localStorage`: si un XSS puede
 * leerlo, el token deja de servir para nada. Vive lo que vive la pestana, y
 * se recupera de `GET /auth/yo` al recargar.
 */

/**
 * Donde vive la API.
 *
 * En desarrollo queda vacia y las peticiones van a `/api`, que el proxy de
 * Vite reenvia: asi el navegador ve un solo origen y la cookie de sesion
 * funciona sin CORS de por medio.
 *
 * En produccion la web esta en Cloudflare Pages y la API en Fly.io, que son
 * dominios distintos, asi que hace falta la URL completa. Se inyecta al
 * compilar con `VITE_API_URL`.
 */
const BASE = (import.meta.env['VITE_API_URL'] ?? '').replace(/\/$/, '');

let csrf: string | null = null;

export function tokenCsrf(): string | null {
  return csrf;
}

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    mensaje: string,
    readonly detalles?: Array<{ campo: string; problema: string }>,
    /**
     * Segundos del `Retry-After`, si vino. Lo mandan el 429 y el 503 de
     * Patroclo: con esto la pantalla dice «espera un momento» o «vuelve
     * mañana» en vez de un error a secas.
     */
    readonly reintentarEnS: number | null = null,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }
}

/**
 * Abre la conexion con la API antes de que nadie la necesite.
 *
 * **Por que existe:** la primera peticion a la API desde una maquina que lleva
 * un rato sin hablarle **falla**, y la segunda funciona. Se reprodujo el
 * 19/09/2026: la primera tarda 12,6 s y muere; las cuatro siguientes tardan
 * 0,2 s. Pasa igual en el navegador, en `curl` y en `flyctl` —que ni siquiera
 * habla con esta aplicacion—, asi que **no es del sistema**: es algo en el
 * camino entre el equipo y Fly.
 *
 * No se puede arreglar desde aqui, pero si se puede pagar ese primer golpe
 * **antes de que el usuario escriba su contrasena** en vez de encima de ella.
 * `/api/salud` es publica, no cuenta contra el limite de peticiones y no toca
 * sesion, asi que es la candidata natural.
 *
 * Si falla, da igual: nadie la esta esperando.
 */
export function calentarConexion(): void {
  void fetch(`${BASE}/api/salud`, { credentials: 'include' }).catch(() => {
    // Justo el caso que esto existe para absorber. Se reintenta una vez, que
    // es lo que basta segun lo medido.
    void fetch(`${BASE}/api/salud`, { credentials: 'include' }).catch(() => {});
  });
}

async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const escribe = (opciones.method ?? 'GET') !== 'GET';

  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}/api${ruta}`, {
      ...opciones,
      credentials: 'include',
      headers: {
        ...(opciones.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(escribe && csrf ? { 'X-CSRF-Token': csrf } : {}),
        ...opciones.headers,
      },
    });
  } catch {
    /*
     * `fetch` solo lanza cuando la peticion **no llego a ninguna parte**: no hay
     * respuesta, ni siquiera un error del servidor. El navegador lo cuenta como
     * «Failed to fetch», que no le dice nada a nadie.
     *
     * No se reintenta automaticamente y es a proposito: una escritura que
     * fallo asi **pudo haberse ejecutado igual** —la peticion pudo llegar y
     * perderse la respuesta— y repetirla a ciegas duplicaria un despacho o un
     * movimiento de inventario. Se dice lo que paso y decide la persona.
     */
    throw new ErrorApi(
      0,
      'No se pudo conectar con el servidor. Vuelve a intentarlo: si la primera vez ' +
        'falla y la segunda funciona, es la conexión y no tus datos.',
    );
  }

  if (respuesta.status === 204) return undefined as T;

  const cuerpo: unknown = await respuesta.json().catch(() => null);

  if (!respuesta.ok) {
    const d = cuerpo as { message?: string; mensaje?: string; detalles?: Array<{ campo: string; problema: string }> } | null;
    const reintentar = Number(respuesta.headers.get('retry-after'));
    throw new ErrorApi(
      respuesta.status,
      d?.mensaje ?? d?.message ?? `Error ${respuesta.status}`,
      d?.detalles,
      Number.isFinite(reintentar) && reintentar > 0 ? reintentar : null,
    );
  }

  return cuerpo as T;
}

// --- Identidad -------------------------------------------------------------

export async function ingresar(datos: Credenciales): Promise<{
  usuario: UsuarioSesion;
  debeCambiarClave: boolean;
}> {
  const r = await pedir<{ usuario: UsuarioSesion; debeCambiarClave: boolean; csrf: string }>(
    '/auth/ingresar',
    { method: 'POST', body: JSON.stringify(datos) },
  );
  csrf = r.csrf;
  return { usuario: r.usuario, debeCambiarClave: r.debeCambiarClave };
}

export async function quienSoy(): Promise<UsuarioSesion & { debeCambiarClave: boolean }> {
  const r = await pedir<{
    usuario: UsuarioSesion;
    csrf: string;
    debeCambiarClave: boolean;
  }>('/auth/yo');
  csrf = r.csrf;
  return { ...r.usuario, debeCambiarClave: r.debeCambiarClave };
}

/**
 * Cambiar la propia clave.
 *
 * Exige la actual aunque la sesion este abierta: una sesion prestada en un
 * equipo sin bloquear no debe poder quedarse con la cuenta.
 */
export function cambiarClave(claveActual: string, claveNueva: string): Promise<void> {
  return pedir('/auth/clave', {
    method: 'POST',
    body: JSON.stringify({ claveActual, claveNueva }),
  });
}

// --- Usuarios (solo administrador) -----------------------------------------

export function listarUsuarios(): Promise<UsuarioResumen[]> {
  return pedir('/usuarios');
}

export function crearUsuario(datos: CrearUsuario): Promise<UsuarioResumen> {
  return pedir('/usuarios', { method: 'POST', body: JSON.stringify(datos) });
}

export function cambiarEstadoUsuario(id: string, datos: CambiarEstadoUsuario): Promise<void> {
  return pedir(`/usuarios/${id}/estado`, { method: 'POST', body: JSON.stringify(datos) });
}

export function reiniciarClaveDe(id: string, datos: ReiniciarClave): Promise<void> {
  return pedir(`/usuarios/${id}/clave`, { method: 'POST', body: JSON.stringify(datos) });
}

export async function salir(): Promise<void> {
  await pedir<void>('/auth/salir', { method: 'POST' });
  csrf = null;
}

// --- Ordenes ---------------------------------------------------------------

/**
 * La fila de la lista, tal como la define el contrato.
 *
 * Era una interfaz copiada a mano, y la copia se quedo vieja: declaraba tres
 * estados cuando el contrato ya tiene cinco. Un tipo duplicado es un contrato
 * que solo se cumple hasta que alguien olvide actualizar la copia.
 */
export type FilaOrden = OrdenResumen;

export function listarOrdenes(parametros: Record<string, string>): Promise<{
  datos: FilaOrden[];
  total: number;
  pagina: number;
  tamano: number;
}> {
  const q = new URLSearchParams(
    Object.entries(parametros).filter(([, v]) => v !== '' && v !== undefined),
  );
  return pedir(`/ordenes?${q.toString()}`);
}

export function detalleOrden(id: string): Promise<OrdenDetalle> {
  return pedir(`/ordenes/${id}`);
}

export function anularOrden(id: string, motivo: string): Promise<void> {
  return pedir(`/ordenes/${id}/anular`, { method: 'POST', body: JSON.stringify({ motivo }) });
}

/**
 * Registrar —o corregir— lo que el cliente recibio.
 *
 * **No manda el estado.** ENTREGADA o ENTREGADA_PARCIAL las decide el servidor
 * comparando lo recibido contra lo pedido: no hay forma de equivocarse porque
 * no hay nada que elegir.
 */
export function registrarRecepcion(id: string, datos: RegistrarRecepcion): Promise<void> {
  return pedir(`/ordenes/${id}/recepcion`, { method: 'POST', body: JSON.stringify(datos) });
}

export function resolverIncidencia(id: string, nota: string): Promise<void> {
  return pedir(`/incidencias/${id}/resolver`, { method: 'POST', body: JSON.stringify({ nota }) });
}

export function buscar(q: string): Promise<ResultadoBusqueda> {
  return pedir(`/buscar?q=${encodeURIComponent(q)}`);
}

/**
 * El cuerpo del correo es opcional y solo aplica al canal correo: **es el unico
 * sitio donde viene la ventana horaria**, y trae el importe con que se
 * contrasta el total del PDF.
 */
export function importar(archivos: File[], cuerpoCorreo?: string): Promise<ResultadoImportacion> {
  const cuerpo = new FormData();
  for (const a of archivos) cuerpo.append('archivos', a);
  if (cuerpoCorreo && cuerpoCorreo.trim() !== '') cuerpo.append('cuerpoCorreo', cuerpoCorreo);
  return pedir('/ordenes/importar', { method: 'POST', body: cuerpo });
}

/**
 * Descarga el documento crudo de una orden.
 *
 * Va por `fetch` y no por un enlace normal porque la cookie de sesion tiene
 * que viajar y en produccion la web y la API estan en dominios distintos. El
 * blob se libera despues de usarlo: si no, la pestana se queda con el archivo
 * entero en memoria hasta que se recargue.
 */
export async function descargarArchivo(
  ordenId: string,
  archivoId: string,
  nombre: string,
): Promise<void> {
  const respuesta = await fetch(`${BASE}/api/ordenes/${ordenId}/archivos/${archivoId}`, {
    credentials: 'include',
  });
  if (!respuesta.ok) {
    throw new ErrorApi(respuesta.status, 'No se pudo descargar el archivo');
  }

  const blob = await respuesta.blob();
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  enlace.click();
  URL.revokeObjectURL(url);
}

/** `17` + `KG` -> `17 KG`. La unidad va siempre pegada a la cantidad. */
export function conUnidad(valor: string | null, unidad: 'UN' | 'KG'): string {
  if (valor === null) return '—';
  const n = new Intl.NumberFormat('es-CO').format(Number(valor));
  return unidad === 'KG' ? `${n} kg` : n;
}

// --- Formato ---------------------------------------------------------------

const PESOS = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0,
});

export function pesos(valor: string | null): string {
  if (valor === null) return '—';
  return PESOS.format(Number(valor));
}

/** Una fecha de negocio se muestra tal cual: sin `new Date`, que la corre un dia. */
export function fecha(iso: string): string {
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

export function cantidad(valor: string | null): string {
  if (valor === null) return '—';
  return new Intl.NumberFormat('es-CO').format(Number(valor));
}

// --- Cartera: el puente factura → pago -------------------------------------

/**
 * F6 y F7. **Es el flujo que justifica el sistema entero.**
 *
 * Ninguna de estas funciones manda un total calculado: el esperado, el
 * recaudado y sobre todo la diferencia sin explicar los calcula el servidor a
 * partir de los hechos. Un dato derivado no se digita ni se transporta.
 */
export function listarFacturas(parametros: Record<string, string>): Promise<{
  datos: FacturaResumen[];
  total: number;
  pagina: number;
  tamano: number;
}> {
  const q = new URLSearchParams(
    Object.entries(parametros).filter(([, v]) => v !== '' && v !== undefined),
  );
  return pedir(`/facturas?${q.toString()}`);
}

export function detalleFactura(id: string): Promise<FacturaDetalle> {
  return pedir(`/facturas/${id}`);
}

export function registrarFactura(datos: RegistrarFactura): Promise<{ id: string }> {
  return pedir('/facturas', { method: 'POST', body: JSON.stringify(datos) });
}

export function anularFactura(id: string, motivo: string): Promise<void> {
  return pedir(`/facturas/${id}/anular`, { method: 'POST', body: JSON.stringify({ motivo }) });
}

/** Si la facturación es estricta, y si quien mira puede cambiarlo. */
export function configuracionFacturacion(): Promise<ConfiguracionFacturacion> {
  return pedir('/facturacion/configuracion');
}

export function cambiarFacturacionEstricta(estricta: boolean): Promise<void> {
  return pedir('/facturacion/configuracion', {
    method: 'POST',
    body: JSON.stringify({ estricta }),
  });
}

/** Las órdenes que hoy se pueden facturar. La regla la aplica la API, no la pantalla. */
export function ordenesFacturables(): Promise<OrdenesFacturables> {
  return pedir('/facturacion/ordenes');
}

export function listarRecaudos(parametros: Record<string, string>): Promise<{
  datos: RecaudoResumen[];
  total: number;
  pagina: number;
  tamano: number;
}> {
  const q = new URLSearchParams(
    Object.entries(parametros).filter(([, v]) => v !== '' && v !== undefined),
  );
  return pedir(`/recaudos?${q.toString()}`);
}

export function registrarRecaudo(datos: RegistrarRecaudo): Promise<{ id: string }> {
  return pedir('/recaudos', { method: 'POST', body: JSON.stringify(datos) });
}

export function listarTiposDeduccion(): Promise<TipoDeduccion[]> {
  return pedir('/tipos-deduccion');
}

export function crearTipoDeduccion(datos: CrearTipoDeduccion): Promise<{ id: string }> {
  return pedir('/tipos-deduccion', { method: 'POST', body: JSON.stringify(datos) });
}

export function borrarTipoDeduccion(id: string): Promise<void> {
  return pedir(`/tipos-deduccion/${id}`, { method: 'DELETE' });
}

export function registrarDeduccion(datos: RegistrarDeduccion): Promise<{ id: string }> {
  return pedir('/deducciones', { method: 'POST', body: JSON.stringify(datos) });
}

export function reclamarDeduccion(id: string, nota: string): Promise<void> {
  return pedir(`/deducciones/${id}/reclamar`, { method: 'POST', body: JSON.stringify({ nota }) });
}

/** ¿Está configurada la integración con Siigo? Devuelve nombres, nunca valores. */
export function estadoSiigo(): Promise<EstadoSiigo> {
  return pedir('/siigo/estado');
}

/**
 * Traer de Siigo las facturas emitidas en un rango.
 *
 * **Solo lee.** Escribir en Siigo está aparcado a propósito: una factura mal
 * emitida no es un bug, es un problema tributario.
 */
export function importarDeSiigo(desde: string, hasta: string): Promise<ResultadoImportacionSiigo> {
  return pedir('/siigo/importar', { method: 'POST', body: JSON.stringify({ desde, hasta }) });
}

// --- Conciliación: qué factura va con qué orden ----------------------------

/**
 * La bandeja de lo que falta cuadrar.
 *
 * Cada factura trae **la sugerencia ya calculada** por el servidor. Hacerlo
 * aquí obligaría a bajarse todas las órdenes al navegador para responder una
 * pregunta que la base contesta de una.
 */
export function verConciliacion(): Promise<Conciliacion> {
  return pedir('/conciliacion');
}

/**
 * Enlazar una factura que ya existe con sus órdenes.
 *
 * **Reemplaza el conjunto entero.** Una lista vacía desenlaza, que es cómo se
 * deshace un enlace equivocado.
 */
export function enlazarOrdenes(
  facturaId: string,
  ordenes: Array<{ ordenId: string; valorImputado: string }>,
): Promise<void> {
  return pedir(`/facturas/${facturaId}/ordenes`, {
    method: 'POST',
    body: JSON.stringify({ ordenes }),
  });
}

/** Enlazar de una vez las que no tienen duda. Solo esas. */
export function conciliarSinDuda(): Promise<{ enlazadas: number; revisadas: number }> {
  return pedir('/conciliacion/sin-duda', { method: 'POST' });
}

// --- Avisos de cobro -------------------------------------------------------

/**
 * Cargar cartas de cesión y pegar avisos de pago.
 *
 * **Un aviso no registra un pago.** Dice qué facturas van a pagar y cuándo; que
 * la plata entró lo confirma una persona.
 */
export function cargarAvisos(
  archivos: File[],
  cuerpoCorreo?: string,
): Promise<ResultadoImportacionAvisos> {
  const cuerpo = new FormData();
  for (const a of archivos) cuerpo.append('archivos', a);
  if (cuerpoCorreo && cuerpoCorreo.trim() !== '') cuerpo.append('cuerpoCorreo', cuerpoCorreo);
  return pedir('/avisos/cargar', { method: 'POST', body: cuerpo });
}

/** Lo comprometido y todavía sin confirmar. */
export function avisosPendientes(): Promise<AvisoResumen[]> {
  return pedir('/avisos/pendientes');
}

/**
 * Volver a intentar los cruces que quedaron sueltos.
 *
 * Un cruce que hoy no se pudo hacer —porque Siigo limitó las peticiones, o
 * porque la factura no estaba— puede hacerse mañana.
 */
export function reintentarCruces(): Promise<{ revisadas: number; cruzadas: number }> {
  return pedir('/avisos/reintentar-cruces', { method: 'POST' });
}

/** «Se pagó el 100 %»: una persona confirma que la plata entró. */
export function confirmarPago(datos: ConfirmarPago): Promise<{ recaudoId: string }> {
  return pedir('/avisos/confirmar', { method: 'POST', body: JSON.stringify(datos) });
}

/** Mandar el recibo de caja a Siigo. Idempotente: reintentar no crea un segundo. */
export function enviarReciboSiigo(recaudoId: string): Promise<ReciboEnviado> {
  return pedir(`/recaudos/${recaudoId}/recibo-siigo`, { method: 'POST' });
}

// --- F2: el motor de fechas -------------------------------------------------

/**
 * Qué día cabe esta orden, según el calendario del CEDI.
 *
 * Solo lectura y sin efectos: no reserva la cita —eso se hace en C3—, contesta
 * qué día pedirla.
 */
export function agendaDeOrden(id: string): Promise<AgendaOrden> {
  return pedir(`/ordenes/${id}/agenda`);
}

/**
 * Todo lo del camión en una sola consulta: placa, año modelo, pico y placa y
 * días bloqueados. Es una sola pantalla y no tiene sentido pedirlo en trozos.
 */
export function configuracionCamion(): Promise<ConfiguracionCamion> {
  return pedir('/camion');
}

export function crearVehiculo(datos: GuardarVehiculo): Promise<{ id: string }> {
  return pedir('/camion/vehiculos', { method: 'POST', body: JSON.stringify(datos) });
}

export function cambiarEstadoVehiculo(id: string, activo: boolean): Promise<void> {
  return pedir(`/camion/vehiculos/${id}/estado`, {
    method: 'POST',
    body: JSON.stringify({ activo }),
  });
}

/** Crear el del semestre siguiente **cierra el anterior**: no se editan. */
export function crearRestriccion(datos: CrearRestriccion): Promise<{ id: string }> {
  return pedir('/camion/restricciones', { method: 'POST', body: JSON.stringify(datos) });
}

export function cerrarRestriccion(id: string, datos: CerrarRestriccion): Promise<void> {
  return pedir(`/camion/restricciones/${id}/cerrar`, {
    method: 'POST',
    body: JSON.stringify(datos),
  });
}

export function marcarDiaSinCamion(datos: MarcarDiaSinCamion): Promise<{ id: string }> {
  return pedir('/camion/dias-sin-camion', { method: 'POST', body: JSON.stringify(datos) });
}

export function quitarDiaSinCamion(id: string): Promise<void> {
  return pedir(`/camion/dias-sin-camion/${id}`, { method: 'DELETE' });
}

// --- F9: el inventario -------------------------------------------------------

/**
 * Saldos, catálogo y últimos movimientos, de una vez.
 *
 * Una sola petición a propósito: se usa desde un teléfono en la planta, y tres
 * viajes serían tres esperas.
 */
export function estadoInventario(ubicacion?: string): Promise<EstadoInventario> {
  return pedir(`/inventario${ubicacion ? `?ubicacion=${ubicacion}` : ''}`);
}

export function registrarMovimiento(datos: RegistrarMovimiento): Promise<{ id: string }> {
  return pedir('/inventario/movimientos', { method: 'POST', body: JSON.stringify(datos) });
}

/** Empacar: sale granel y entra presentación, en la misma operación. */
export function empacar(datos: Empacar): Promise<{ operacionId: string }> {
  return pedir('/inventario/empacar', { method: 'POST', body: JSON.stringify(datos) });
}

/**
 * **Enviar y recibir son dos peticiones**, no una partida en dos.
 *
 * Las hace gente distinta, en sitios distintos y en dias distintos. Que fueran
 * una sola decia que el queso se teletransporta.
 */
export function enviar(datos: Enviar): Promise<{ id: string }> {
  return pedir('/inventario/enviar', { method: 'POST', body: JSON.stringify(datos) });
}

export function recibir(datos: Recibir): Promise<{ id: string }> {
  return pedir('/inventario/recibir', { method: 'POST', body: JSON.stringify(datos) });
}

/** Producir: sale queso de la cuba y se gasta lo que entro en ella. */
export function producir(datos: Producir): Promise<{ operacionId: string }> {
  return pedir('/inventario/producir', { method: 'POST', body: JSON.stringify(datos) });
}

/** **Corregir anota lo contrario**, no cambia lo anotado. Exige motivo. */
export function corregirMovimiento(
  id: string,
  datos: CorregirMovimiento,
): Promise<{ id: string }> {
  return pedir(`/inventario/movimientos/${id}/corregir`, {
    method: 'POST',
    body: JSON.stringify(datos),
  });
}

// --- F4: alistar y despachar -------------------------------------------------

/** Las órdenes que están por salir de la bodega. */
export function ordenesPorAlistar(): Promise<OrdenPorAlistar[]> {
  return pedir('/alistamiento');
}

/** Qué hay que tomar de una orden, cruzado con lo que hay en la bodega. */
export function alistamientoDeOrden(id: string): Promise<AlistamientoOrden> {
  return pedir(`/alistamiento/${id}`);
}

/**
 * El camión sale. **Lo único de F4 que escribe en el libro de inventario.**
 *
 * Se manda lo que de verdad se cargó, no lo que se pidió.
 */
export function despacharOrden(id: string, datos: DespacharOrden): Promise<{ movimientos: number }> {
  return pedir(`/alistamiento/${id}/despachar`, { method: 'POST', body: JSON.stringify(datos) });
}

// --- F3: el CAU ------------------------------------------------------------

/**
 * El catálogo de motivos, servido por el backend.
 *
 * **No se escribe en la pantalla a propósito**: son los textos exactos de la
 * plantilla oficial, y dos llevan espacios que se pierden fácil al copiarlos.
 */
export function motivosCau(): Promise<MotivoCau[]> {
  return pedir('/cau/motivos');
}

export function listarProrrogas(): Promise<ProrrogaResumen[]> {
  return pedir('/cau');
}

/**
 * Las órdenes que no se pueden entregar el día que piden.
 *
 * La lista de trabajo del CAU: el sistema ya sabe cuáles son, así que no hay que
 * buscarlas en un desplegable de doscientas.
 */
export function candidatasCau(): Promise<CandidataCau[]> {
  return pedir('/cau/candidatas');
}

export function crearProrroga(datos: CrearProrroga): Promise<{ id: string }> {
  return pedir('/cau', { method: 'POST', body: JSON.stringify(datos) });
}

export function marcarRadicada(id: string, numeroRadicado: string): Promise<void> {
  return pedir(`/cau/${id}/radicada`, { method: 'POST', body: JSON.stringify({ numeroRadicado }) });
}

export function registrarRespuestaCau(id: string, datos: RegistrarRespuestaCau): Promise<void> {
  return pedir(`/cau/${id}/respuesta`, { method: 'POST', body: JSON.stringify(datos) });
}

/**
 * Descarga el Excel del CAU.
 *
 * Va por `fetch` y no por un enlace normal porque la cookie de sesión tiene que
 * viajar y en producción la web y la API están en dominios distintos. El blob se
 * libera después de usarlo: si no, la pestaña se queda con el archivo entero en
 * memoria hasta que se recargue.
 */
export async function descargarCau(ids: string[], formato: FormatoCau): Promise<void> {
  const respuesta = await fetch(`${BASE}/api/cau/generar`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
    },
    body: JSON.stringify({ ids, formato }),
  });

  if (!respuesta.ok) {
    const d = (await respuesta.json().catch(() => null)) as { mensaje?: string } | null;
    throw new ErrorApi(respuesta.status, d?.mensaje ?? 'No se pudo generar el archivo');
  }

  const nombre =
    /filename="([^"]+)"/.exec(respuesta.headers.get('Content-Disposition') ?? '')?.[1] ??
    'CAU.xlsx';
  const blob = await respuesta.blob();
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  enlace.click();
  URL.revokeObjectURL(url);
}

// --- F8: el cierre de mes --------------------------------------------------

/**
 * Todo el cierre de un mes, en una petición.
 *
 * Sin `periodo` abre en el mes más reciente del que haya algo: empezar en el
 * mes corriente dejaría la pantalla vacía casi siempre, porque las órdenes de
 * este mes todavía no se han entregado.
 */
export function cierreDelMes(periodo?: string): Promise<CierreDelMes> {
  return pedir(`/cierre${periodo ? `?periodo=${periodo}` : ''}`);
}

/**
 * Las líneas detrás de una cifra.
 *
 * **Toda cifra tiene que poder abrirse hasta el dato.** Una que no se puede
 * auditar no se usa: se desconfía de ella.
 */
export function detalleDelIndicador(
  periodo: string,
  presentacionId?: string,
): Promise<DetalleDelIndicador> {
  const p = new URLSearchParams({ periodo });
  if (presentacionId) p.set('presentacionId', presentacionId);
  return pedir(`/cierre/detalle?${p.toString()}`);
}

/** El .xlsx que manda el Éxito. Varios a la vez, con resultado por archivo. */
export function cargarReportesOtif(archivos: File[]): Promise<ResultadoImportacionOtif> {
  const cuerpo = new FormData();
  for (const a of archivos) cuerpo.append('archivos', a);
  return pedir('/cierre/otif/reportes', { method: 'POST', body: cuerpo });
}

/**
 * Volver a contrastar lo nuestro contra lo del cliente.
 *
 * Mismo botón que «Reintentar los cruces», y por la misma razón: un desacuerdo
 * de hoy puede dejar de serlo mañana, cuando alguien registre la recepción que
 * faltaba.
 */
export function contrastarOtif(periodo: string): Promise<{ incidenciasAbiertas: number }> {
  return pedir('/cierre/contrastar', { method: 'POST', body: JSON.stringify({ periodo }) });
}

/** Resolver no es borrar: es dejar constancia de que una persona lo miró. */
export function resolverIncidenciaDesempeno(id: string, nota: string): Promise<{ id: string }> {
  return pedir(`/cierre/incidencias/${id}/resolver`, {
    method: 'POST',
    body: JSON.stringify({ nota }),
  });
}

/** Anular un reporte cargado por error. Con motivo, y eso libera el mes. */
export function anularReporteOtif(id: string, motivo: string): Promise<{ id: string }> {
  return pedir(`/cierre/otif/reportes/${id}/anular`, {
    method: 'POST',
    body: JSON.stringify({ motivo }),
  });
}

/**
 * Una proporción como porcentaje. `null` se escribe «no se sabe», no «0 %».
 *
 * Es la distinción que más caro costaría perder aquí: un 0 % se ve
 * perfectamente creíble y significaría que no se entregó nada, cuando lo que
 * pasa es que nadie ha registrado todavía qué llegó.
 */
export function porcentaje(valor: string | null): string {
  if (valor === null) return 'no se sabe';
  return `${new Intl.NumberFormat('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(valor) * 100)} %`;
}

// --- Etapa 7: las devoluciones ---------------------------------------------

/**
 * Qué se puede devolver de una orden, qué se devolvió ya, y con qué motivos.
 *
 * `motivos` puede venir vacío, y eso **no es un fallo**: el catálogo nace vacío
 * porque nadie ha dicho todavía cuáles son los motivos reales de devolución.
 */
export function devolucionesDeOrden(ordenId: string): Promise<DevolucionesDeOrden> {
  return pedir(`/ordenes/${ordenId}/devoluciones`);
}

/**
 * Registrar una devolución.
 *
 * **No cambia el estado de entrega.** La entrega fue la que fue; esto es un
 * hecho nuevo, con su fecha y su motivo.
 */
export function registrarDevolucion(datos: RegistrarDevolucion): Promise<{ id: string }> {
  return pedir('/devoluciones', { method: 'POST', body: JSON.stringify(datos) });
}

export function motivosDevolucion(): Promise<MotivoDevolucion[]> {
  return pedir('/devoluciones/motivos');
}

/** Crear un motivo. De él cuelga si el producto reentra a bodega, y eso mueve inventario. */
export function crearMotivoDevolucion(datos: CrearMotivoDevolucion): Promise<{ id: string }> {
  return pedir('/devoluciones/motivos', { method: 'POST', body: JSON.stringify(datos) });
}

/** Borrar un motivo de verdad, mientras no esté en uso. Un catálogo no es un hecho. */
export function borrarMotivoDevolucion(id: string): Promise<{ borrado: true }> {
  return pedir(`/devoluciones/motivos/${id}`, { method: 'DELETE' });
}

// --- Lo que cuesta el sistema (E8-02) ----------------------------------------

/** Neon, Fly.io, Anthropic y Voyage, mes por mes. Solo administrador y desarrollador. */
export function resumenDeGastos(meses = 6): Promise<ResumenDeGastos> {
  return pedir(`/gastos?meses=${meses}`);
}

/** Digitar (o corregir) la factura del mes de un proveedor. Queda auditado. */
export function registrarFacturaGasto(datos: RegistrarFacturaGasto): Promise<{ id: string }> {
  return pedir('/gastos/factura', { method: 'POST', body: JSON.stringify(datos) });
}

const DOLARES = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Dólares, con coma decimal. `null` es «sin cifra», nunca US$0,00. */
export function dolares(valor: string | null): string {
  if (valor === null) return 'sin cifra';
  return DOLARES.format(Number(valor));
}

// --- Patroclo, el asistente (E8-01) ------------------------------------------
//
// Todo pasa por `/api/patroclo/*`: el navegador nunca le habla a Patroclo. La
// sesión y el CSRF son los de siempre; la identidad que ve Patroclo la arma la
// API desde la sesión, no este cliente.

export function preguntarAPatroclo(datos: PreguntaPatroclo): Promise<RespuestaPatroclo> {
  return pedir('/patroclo/chat', { method: 'POST', body: JSON.stringify(datos) });
}

export function conversacionesPatroclo(): Promise<ConversacionResumen[]> {
  return pedir('/patroclo/conversaciones');
}

export function conversacionPatroclo(id: string): Promise<ConversacionCompleta> {
  return pedir(`/patroclo/conversaciones/${id}`);
}

export function borrarConversacionPatroclo(id: string): Promise<void> {
  return pedir(`/patroclo/conversaciones/${id}`, { method: 'DELETE' });
}

// --- La base de conocimiento de Patroclo (solo administrador y desarrollador)

export function documentosPatroclo(): Promise<DocumentoPatroclo[]> {
  return pedir('/patroclo/documentos');
}

/** El documento y sus fragmentos, en orden: lo que Patroclo puede recuperar de él. */
export function documentoPatroclo(id: string): Promise<DocumentoCompleto> {
  return pedir(`/patroclo/documentos/${id}`);
}

export function subirDocumentoPatroclo(
  archivo: File,
  visibilidad: VisibilidadDocumento,
): Promise<ResultadoSubidaDocumento> {
  const cuerpo = new FormData();
  cuerpo.append('archivo', archivo);
  cuerpo.append('visibilidad', visibilidad);
  return pedir('/patroclo/documentos', { method: 'POST', body: cuerpo });
}

export function borrarDocumentoPatroclo(id: string): Promise<void> {
  return pedir(`/patroclo/documentos/${id}`, { method: 'DELETE' });
}

// --- Las trazas de Patroclo: qué se preguntó y cuánto costó (§5.1) ----------

/** Solo los filtros que tienen valor: uno vacío no viaja. */
function consulta(filtro: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(filtro)) if (v !== undefined && v !== '') q.set(k, String(v));
  const texto = q.toString();
  return texto ? `?${texto}` : '';
}

export function trazasPatroclo(filtro: FiltroTrazas): Promise<PaginaTrazas> {
  return pedir(`/patroclo/trazas${consulta(filtro)}`);
}

export function resumenTrazasPatroclo(rango: RangoTrazas): Promise<ResumenTrazas> {
  return pedir(`/patroclo/trazas/resumen${consulta(rango)}`);
}

export function trazaPatroclo(id: string): Promise<TrazaCompleta> {
  return pedir(`/patroclo/trazas/${id}`);
}

/**
 * El archivo tal como se subió. Por `fetch` y no por un enlace, como
 * `descargarArchivo`: la cookie de sesión tiene que viajar, y en producción la
 * web y la API están en dominios distintos.
 */
export async function descargarOriginalPatroclo(id: string, nombre: string): Promise<void> {
  const respuesta = await fetch(`${BASE}/api/patroclo/documentos/${id}/original`, { credentials: 'include' });
  if (!respuesta.ok) {
    const d = (await respuesta.json().catch(() => null)) as { mensaje?: string } | null;
    throw new ErrorApi(respuesta.status, d?.mensaje ?? 'No se pudo bajar el original');
  }
  const blob = await respuesta.blob();
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  enlace.click();
  URL.revokeObjectURL(url);
}
